import React, {
    Fragment,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { getFormSyncErrors, reset, submit } from 'redux-form';
import Typography from '@material-ui/core/Typography';
import { RemoveRedEye } from '@material-ui/icons';
import { useParams } from 'react-router-dom';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import TitleWithUnderline from '../../layout/TitleWithUnderline';
import RegisterForm from './RegisterForm';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import * as ApiEvent from '../../../api/ApiEvent';
import UseLoadingState from '../../../control/loading/UseLoadingState';
import { checkResStatusService } from '../../../utilities/ResServiceUtils';
import * as AlertMSGConstants from '../../../constants/AlertMSGConstants';
import Loading from '../../../control/loading/Loading';
import {
    convertDateDisplayFullAbbr,
    displayTimeRange,
} from '../../../utilities/DatetimeUtils';
import moment from 'moment';
import PublicDetail from '../PublicDetail';
import ControlButton from '../../../control/button/Button';
import AlertModal from '../AlertModal';
import { checkWidthDown } from '../../../utilities/Utils';
import { withWidth } from '@material-ui/core';
import ModalImageFullScreen from '../../event/ModalImageFullscreen';
import GetAppIcon from '@material-ui/icons/GetApp';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '50%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    },
    rootPaper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // minHeight: 'calc(100% - 48px)',
        minHeight: '100%',
        // margin: '24px',
        // [theme.breakpoints.down('sm')]: {
        //     width: 'calc(98% - 48px)',
        // },
        ...theme.paperPadding,
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    expandBtn: {
        '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
        },
    },
    btnExpandText: {
        color: theme.palette.primary.main,
        fontSize: '24px',
        fontWeight: '600',
        textDecoration: 'underline',
        textDecorationColor: theme.palette.primary.main,
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
    },
    btnExpandIcon: {
        color: theme.palette.primary.main,
        fontSize: '36px',
    },

    bannerWrapper: {
        position: 'relative',
        overflow: 'hidden',
        background: 'gainsboro',
        height: '377px',
        '&:hover $overlayButton': {
            opacity: 1,
            pointerEvents: 'auto',
        },
    },
    image: {
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            height: '100%',
        },
        objectFit: 'cover',
        objectPosition: 'top',
    },
    imageModal: {
        width: '100%',
        height: '100%',
        objectFit: 'scale-down',
        objectPosition: 'top',
    },
    overlayButton: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0,
        transition: 'opacity 0.3s ease',
        backgroundColor: 'rgba(0,0,0,0.3)',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        cursor: 'pointer',
        pointerEvents: 'none',
        width: '53px',
        height: '53px',
    },
    eventNotFound: {
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'center',
        height: '100%',
        textAlign: 'center',
        flexGrow: 1,
    },
    eventNotFoundText: {
        color: '#EA4848',
        fontSize: '34px',
        fontWeight: '700',
    },
    eventName: {
        fontSize: '34px',
        fontWeight: '700',
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        wordBreak: 'break-all',
    },
    noImage: {
        width: '100%',
        height: '100%',
        background: 'gainsboro',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '40px',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    notOpenInfo: {
        color: '#424242',
        fontSize: '34px',
        fontWeight: '700',
        textAlign: 'center',
        padding: '24px 0px 8px 0px',
        lineHeight: '2.5rem',
    },
    notOpenInfoDesc: {
        color: '#424242',
        fontSize: '18px',
        fontWeight: '400',
        textAlign: 'center',
    },
    downloadBtn: {
        backgroundColor: '#F78427',
        color: '#FFFFFF',
        height: '40px',
        borderRadius: '30px',
        fontSize: '20px',
        fontWeight: '700',
        width: '200px',
        justifyContent: 'space-around',
        padding: '8px',
    },
    infoExternal: {
        fontSize: '24px',
        fontWeight: '400',
        textAlign: 'center',
    },
    consentInfo: {
        color: '#9E9E9E',
        fontSize: '18px',
        lineHeight: '16px',
    },
    downloadInfo: {
        color: '#9E9E9E',
        fontSize: '24px',
        padding: '24px 0px 24px 0px',
        textAlign: 'center',
    },
}));

const formatEventStartEndDateAbbr = (startDate, endDate) => {
    if (
        isNaN(new Date(startDate).getTime()) ||
        isNaN(new Date(endDate).getTime())
    ) {
        return '-';
    }
    const startDateObj = moment(startDate);
    const endDateObj = moment(endDate);
    if (startDateObj.isSame(endDateObj)) {
        return convertDateDisplayFullAbbr(startDate);
    } else {
        return (
            convertDateDisplayFullAbbr(startDate) +
            ' - ' +
            convertDateDisplayFullAbbr(endDate)
        );
    }
};

const formatEventTime = (startTime, endTime) => {
    if (startTime && endTime) {
        return displayTimeRange(startTime, endTime);
    } else {
        return '-';
    }
};

const formId = 'RegistrationForm';

const PublicEventComponent = (props) => {
    const { width } = props;
    const isSmDowm = checkWidthDown(width, 'sm');
    const dispatch = useDispatch();
    let { eventId } = useParams();
    const classes = useStyles();
    const loadingState = UseLoadingState();

    const [displayEquipment, setDisplayEquipment] = useState(false);
    const [eventData, setEventData] = useState(null);
    const [isFoundEvent, setIsFoundEvent] = useState(true);
    const [open, setOpen] = useState(false);
    const errorState = useSelector((state) => getFormSyncErrors(formId)(state));
    const [errorMessage, setErrorMessage] = useState({
        title: null,
        detail: null,
    });
    const [successMessage, setSuccessMessage] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            loadingState.showLoading();
            const parameters = { eventId };
            const response = await ApiEvent.fetchEventPublicById({
                ...parameters,
            });
            if (response.data) {
                const dateNow = moment();
                const reserveStartDateTime = moment(
                    `${response.data.reserveStartDate} ${response.data.reserveStartTime}`,
                    'YYYY-MM-DD HH:mm'
                );
                const reserveEndDateTime = moment(
                    `${response.data.reserveEndDate} ${response.data.reserveEndTime}`,
                    'YYYY-MM-DD HH:mm'
                );
                const isBetweenReserveStartEnd = dateNow.isBetween(
                    reserveStartDateTime,
                    reserveEndDateTime,
                    'minute',
                    '[]'
                );
                if (
                    response.data.isCanceled ||
                    !response.data.isPublic ||
                    response.data.externalCapacity === 0 ||
                    !isBetweenReserveStartEnd
                ) {
                    setIsFoundEvent(false);
                } else {
                    setEventData(response.data);
                }
            } else {
                setErrorMessage({
                    title: null,
                    detail:
                        checkResStatusService(response) ||
                        AlertMSGConstants.SYSTEM_ERROR,
                });
                setIsFoundEvent(false);
            }
        } catch (err) {
            setIsFoundEvent(false);
        } finally {
            loadingState.hideLoading();
        }
    }, [eventId, loadingState]);

    const checkUserAndEvent = useCallback(async () => {
        // Use custom schema for Android
        // Use AASA (file) for ios
        const deepLink = `${process.env.REACT_APP_CONNEXT_BUNDLE_ID}://event/${eventId}`;

        const isAndroid = /Android/i.test(navigator.userAgent);

        if (isAndroid) {
            const now = Date.now();
            window.location = deepLink;

            setTimeout(() => {
                // If app is not opened within 2 seconds, fallback
                if (Date.now() - now < 2100) {
                    fetchData();
                }
            }, 1500);
        } else {
            fetchData();
        }
    }, [eventId, loadingState]);

    useEffect(() => {
        checkUserAndEvent();
    }, []);

    const handleModalBehavior = useCallback(() => {
        setOpen((prev) => !prev);
    }, [setOpen]);

    const handleClickGetApp = useCallback(() => {
        window.open(
            process.env.REACT_APP_CONNEXT_DOWNLOAD_LINK,
            'popUpWindow',
            'height=600,width=600,left=10,top=10,,scrollbars=yes,menubar=no'
        );
    }, []);

    const onSubmit = useCallback(
        async (formValues) => {
            try {
                loadingState.showLoading();

                // register post external
                const res = await ApiEvent.reserveExternalUser(
                    eventData.eventId,
                    {
                        email: formValues.email,
                        firstName: formValues.firstName,
                        lastName: formValues.lastName,
                        companyName: formValues.companyName,
                        position: formValues.position,
                    }
                );

                if (res.data.reserveId) {
                    // show ลงทะเบียนสำเร็จ modal
                    setSuccessMessage('ลงทะเบียนสำเร็จ');
                } else {
                    //error
                    setErrorMessage({
                        title: null,
                        detail: 'ลงทะเบียนไม่สำเร็จ',
                    });
                }

                loadingState.hideLoading();
            } catch (error) {
                loadingState.hideLoading();
                if (error && Array.isArray(error.errors) && error.errors[0]) {
                    setErrorMessage({
                        title: error.errors[0].title,
                        detail: error.errors[0].detail,
                    });
                } else {
                    setErrorMessage({
                        title: null,
                        detail: AlertMSGConstants.SYSTEM_ERROR,
                    });
                }
            }
        },
        [eventData, loadingState]
    );

    const isPassReservedDateTime = useMemo(() => {
        if (eventData) {
            const dateNow = moment();
            const reserveStartDateTime = moment(
                `${eventData.reserveStartDate} ${eventData.reserveStartTime}`,
                'YYYY-MM-DD HH:mm'
            );
            const reserveEndDateTime = moment(
                `${eventData.reserveEndDate} ${eventData.reserveEndTime}`,
                'YYYY-MM-DD HH:mm'
            );
            const isBetweenReserveStartEnd = dateNow.isBetween(
                reserveStartDateTime,
                reserveEndDateTime,
                'minute',
                '[]'
            );
            return !isBetweenReserveStartEnd;
        } else {
            return false;
        }
    }, [eventData]);

    const isExternalCanReserve = useMemo(() => {
        try {
            if (eventData) {
                if (eventData.isPublic) {
                    const dateNow = moment();
                    const reserveStartDateTime = moment(
                        `${eventData.reserveStartDate} ${eventData.reserveStartTime}`,
                        'YYYY-MM-DD HH:mm'
                    );
                    const reserveEndDateTime = moment(
                        `${eventData.reserveEndDate} ${eventData.reserveEndTime}`,
                        'YYYY-MM-DD HH:mm'
                    );
                    const isBetweenReserveStartEnd = dateNow.isBetween(
                        reserveStartDateTime,
                        reserveEndDateTime,
                        'minute',
                        '[]'
                    );
                    if (isBetweenReserveStartEnd) {
                        return (
                            eventData.externalCapacity === null ||
                            (eventData.externalCapacity > 0 &&
                                eventData.externalReservedCount <
                                    eventData.externalCapacity)
                        );
                    } else {
                        return false;
                    }
                } else {
                    // not public ขึ้นกับรายชื่ออย่างเดียว
                    return false;
                }
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    }, [eventData]);

    const isInternalCanReserve = useMemo(() => {
        if (!eventData) return false;

        const { internalCapacity, internalReservedCount } = eventData;

        return (
            internalCapacity === null ||
            (internalCapacity > 0 &&
                internalReservedCount < internalCapacity &&
                !isPassReservedDateTime)
        );
    }, [eventData, isPassReservedDateTime]);

    const isReserveButtonDisabled = useMemo(() => {
        if (Object.keys(errorState).length === 0) {
            return false;
        } else {
            return true;
        }
    }, [errorState]);

    const handleCloseModalError = useCallback(() => {
        setErrorMessage({ title: null, detail: null });
    }, []);

    const handleCloseModalSuccess = useCallback(async () => {
        setSuccessMessage(null);
        await fetchData();
        dispatch(reset(formId));
    }, [fetchData, dispatch]);

    return (
        <Fragment>
            <Scrollbars universal={true}>
                <Paper className={classes.rootPaper}>
                    <div className={classes.root}>
                        <TitleWithUnderline title="ลงทะเบียนเข้าร่วมกิจกรรม" />
                        {isFoundEvent ? (
                            eventData && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                    }}
                                >
                                    <div className={classes.bannerWrapper}>
                                        {eventData && eventData.coverImage ? (
                                            <>
                                                <img
                                                    src={eventData.coverImage}
                                                    alt="img"
                                                    className={classes.image}
                                                    onClick={
                                                        handleModalBehavior
                                                    }
                                                />
                                                <IconButton
                                                    onClick={
                                                        handleModalBehavior
                                                    }
                                                    className={
                                                        classes.overlayButton
                                                    }
                                                >
                                                    <RemoveRedEye
                                                        color="primary.dark"
                                                        style={{
                                                            fontSize: 24,
                                                            opacity: 0.3,
                                                        }}
                                                    />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <div className={classes.noImage}>
                                                ไม่มีรูป
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography
                                            className={classes.eventName}
                                        >
                                            {eventData.eventName}
                                        </Typography>
                                        <Grid container>
                                            {
                                                <>
                                                    <Grid item xs={12} sm={6}>
                                                        <PublicDetail
                                                            fieldName="วันที่"
                                                            value={formatEventStartEndDateAbbr(
                                                                eventData.eventStartDate,
                                                                eventData.eventEndDate
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <PublicDetail
                                                            fieldName="เวลา"
                                                            value={formatEventTime(
                                                                eventData.eventStartTime,
                                                                eventData.eventEndTime
                                                            )}
                                                        />
                                                    </Grid>
                                                </>
                                            }
                                            <Grid item xs={12}>
                                                <PublicDetail
                                                    fieldName="สถานที่"
                                                    value={
                                                        eventData.venue
                                                            ? eventData.venue
                                                            : '-'
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        {displayEquipment && (
                                            <>
                                                <Grid item>
                                                    <PublicDetail
                                                        fieldName="รายละเอียดกิจกรรม"
                                                        fullWidth
                                                        value={
                                                            eventData.eventDescription
                                                        }
                                                        isHtml={true}
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <PublicDetail
                                                        fieldName="วิทยากร"
                                                        fullWidth
                                                        value={
                                                            eventData.speakers
                                                        }
                                                        isHtml={true}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'start',
                                        }}
                                    >
                                        <Button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setDisplayEquipment(
                                                    !displayEquipment
                                                );
                                            }}
                                            startIcon={
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {displayEquipment ? (
                                                        <ExpandLess
                                                            className={
                                                                classes.btnExpandIcon
                                                            }
                                                        />
                                                    ) : (
                                                        <ExpandMore
                                                            className={
                                                                classes.btnExpandIcon
                                                            }
                                                        />
                                                    )}
                                                </span>
                                            }
                                            style={{
                                                padding: 0,
                                            }}
                                            className={classes.expandBtn}
                                            disableRipple
                                        >
                                            <span
                                                className={
                                                    classes.btnExpandText
                                                }
                                            >
                                                {displayEquipment
                                                    ? 'ซ่อน'
                                                    : 'รายละเอียดเพิ่มเติม'}
                                            </span>
                                        </Button>
                                    </div>
                                    {eventData &&
                                        isInternalCanReserve &&
                                        isExternalCanReserve && (
                                            <>
                                                <Divider />
                                                <Typography
                                                    className={
                                                        classes.downloadInfo
                                                    }
                                                >
                                                    พนักงาน/ลูกจ้างพนักงานการไฟฟ้านครหลวง
                                                    ลงทะเบียนผ่านแอปพลิเคชัน MEA
                                                    Connext ง่ายกว่า
                                                </Typography>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                        paddingBottom: '24px',
                                                    }}
                                                >
                                                    <ControlButton
                                                        variant="contained"
                                                        className={
                                                            classes.downloadBtn
                                                        }
                                                        startIcon={
                                                            <GetAppIcon
                                                                style={{
                                                                    fontSize:
                                                                        '20px',
                                                                }}
                                                            />
                                                        }
                                                        onClick={
                                                            handleClickGetApp
                                                        }
                                                        label={
                                                            'ดาวน์โหลดแอปพลิเคชัน'
                                                        }
                                                    />
                                                </div>
                                            </>
                                        )}
                                    {eventData.isPublic && <Divider />}
                                    {isExternalCanReserve && (
                                        <>
                                            <RegisterForm onSubmit={onSubmit} />{' '}
                                            <Divider
                                                style={{
                                                    backgroundColor: '#f78427',
                                                    marginBottom: '16px',
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                className={classes.consentInfo}
                                            >
                                                เมื่อท่านกด "ลงทะเบียน"
                                                เพื่อเข้าร่วมกิจกรรมนี้
                                                ถือว่าท่านได้ให้ความยินยอมแก่ผู้จัดงานในการเก็บรวบรวม
                                                และใช้ข้อมูลส่วนบุคคลของท่านที่ได้ให้ไว้
                                                เพื่อวัตถุประสงค์ในการดำเนินกิจกรรม
                                                การติดต่อสื่อสาร
                                                การจัดทำรายงานสรุป
                                                และกิจกรรมอื่นๆ ที่เกี่ยวข้อง
                                                ทั้งนี้
                                                ผู้จัดงานจะดำเนินการจัดเก็บและดูแลข้อมูลของท่านตามมาตรการรักษาความปลอดภัยอย่างเหมาะสม
                                            </Typography>
                                        </>
                                    )}
                                    {eventData.isPublic &&
                                        (!isExternalCanReserve ||
                                            isPassReservedDateTime) && (
                                            <div>
                                                <Typography
                                                    className={
                                                        classes.notOpenInfo
                                                    }
                                                >
                                                    ขออภัยในความไม่สะดวก
                                                    <br />
                                                    ขณะนี้กิจกรรมนี้มีผู้สนใจลงทะเบียนล่วงหน้าครบ
                                                    <br /> ตามจำนวนที่กำหนดแล้ว
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.notOpenInfoDesc
                                                    }
                                                >
                                                    หากในอนาคตมีการเพิ่มการรองรับจำนวนผู้เข้าร่วม{' '}
                                                    <br />
                                                    ทางทีมงานจะแจ้งให้ทราบผ่านทางเว็บไซต์และสื่อประชาสัมพันธ์ต่างๆ{' '}
                                                    <br />
                                                    ขอบคุณอย่างยิ่งที่ให้ความสนใจในกิจกรรมของทาง
                                                    กฟน.
                                                </Typography>
                                            </div>
                                        )}
                                    {isExternalCanReserve && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'end',
                                                paddingTop: '16px',
                                            }}
                                        >
                                            <ControlButton
                                                variant="contained"
                                                onClick={() =>
                                                    dispatch(submit(formId))
                                                }
                                                label={'ลงทะเบียน'}
                                                disabled={
                                                    isReserveButtonDisabled
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )
                        ) : (
                            <div className={classes.eventNotFound}>
                                <EventBusyIcon
                                    style={{
                                        color: '#EA4848',
                                        fontSize: 88,
                                        alignSelf: 'center',
                                    }}
                                />
                                <Typography
                                    className={classes.eventNotFoundText}
                                >
                                    ไม่พบข้อมูลกิจกรรม
                                </Typography>
                                <Typography>
                                    กรุณาติดต่อผู้ดูแลกิจกรรม
                                    <br />
                                    <span>เพื่อสอบถามข้อมูลเพิ่มเติม</span>
                                </Typography>
                            </div>
                        )}
                    </div>
                </Paper>
            </Scrollbars>
            <Loading isShowLoading={loadingState.isShowLoading} />
            {eventData && eventData.coverImage && (
                <ModalImageFullScreen
                    img={eventData.coverImage}
                    open={open}
                    onClose={handleModalBehavior}
                />
            )}
            <AlertModal
                handleCloseModalError={handleCloseModalError}
                handleCloseModalSuccess={handleCloseModalSuccess}
                errorTitle={errorMessage.title}
                errorMessage={errorMessage.detail}
                successMessage={successMessage}
            />
        </Fragment>
    );
};

export default withWidth()(PublicEventComponent);
